/** The shape-set library panel: import SVGs, manage sets, capture strokes. */
import type { Engine } from '../core/engine';
import type { ShapeItem, ShapeLibrary } from '../core/shape-library';
import { applyPathToCtx } from '../core/geometry';

const MAX_SVG_BYTES = 2_000_000;

export interface LibraryPanel {
  el: HTMLElement;
  open: () => void;
  refresh: () => void;
  importFiles: (files: FileList | File[]) => Promise<void>;
}

function glyphCanvas(item: ShapeItem): HTMLCanvasElement {
  const SIZE = 30;
  const c = document.createElement('canvas');
  c.width = SIZE;
  c.height = SIZE;
  const ctx = c.getContext('2d');
  if (!ctx) return c;
  ctx.translate(SIZE / 2, SIZE / 2);
  ctx.scale(SIZE * 0.78, SIZE * 0.78); // glyph spans [-0.5, 0.5]
  applyPathToCtx(item.path, ctx);
  ctx.fillStyle = '#e8e8ea';
  ctx.strokeStyle = '#e8e8ea';
  if (item.stroke) {
    ctx.lineWidth = 0.06;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  } else {
    ctx.fill(item.fillRule ?? 'nonzero');
  }
  return c;
}

export function buildLibraryPanel(lib: ShapeLibrary, engine: Engine): LibraryPanel {
  const root = document.createElement('div');
  root.className = 'alc-lib';
  root.hidden = true;

  const card = document.createElement('div');
  card.className = 'alc-lib-card';
  root.appendChild(card);

  const header = document.createElement('div');
  header.className = 'alc-lib-header';
  const title = document.createElement('div');
  title.className = 'alc-lib-title';
  title.textContent = 'Shape sets';
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'alc-lib-close';
  closeBtn.textContent = '×';
  closeBtn.setAttribute('aria-label', 'Close');
  header.append(title, closeBtn);

  const drop = document.createElement('label');
  drop.className = 'alc-lib-drop';
  drop.innerHTML = '<span>Import SVG — drop files here or click</span>';
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = '.svg,image/svg+xml';
  fileInput.multiple = true;
  fileInput.className = 'alc-lib-file';
  drop.appendChild(fileInput);

  const sets = document.createElement('div');
  sets.className = 'alc-lib-sets';

  const footer = document.createElement('div');
  footer.className = 'alc-lib-footer';
  const captureBtn = document.createElement('button');
  captureBtn.type = 'button';
  captureBtn.className = 'alc-lib-btn';
  captureBtn.textContent = 'Capture last stroke → set';
  footer.appendChild(captureBtn);

  const toastEl = document.createElement('div');
  toastEl.className = 'alc-lib-toast';
  toastEl.hidden = true;

  card.append(header, drop, sets, footer, toastEl);

  let toastTimer: ReturnType<typeof setTimeout> | null = null;
  const toast = (msg: string): void => {
    toastEl.textContent = msg;
    toastEl.hidden = false;
    if (toastTimer !== null) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toastEl.hidden = true;
    }, 2600);
  };
  lib.onError = toast;

  const close = (): void => {
    root.hidden = true;
  };
  closeBtn.addEventListener('click', close);
  root.addEventListener('click', (e) => {
    if (e.target === root) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !root.hidden) close();
  });

  const importFiles = async (files: FileList | File[]): Promise<void> => {
    // Each successful import fires lib.onChange → libPanel.refresh, so we don't
    // refresh again here (that would rebuild the list redundantly).
    for (const f of Array.from(files)) {
      if (!/\.svg$/i.test(f.name) && f.type !== 'image/svg+xml') continue;
      if (f.size > MAX_SVG_BYTES) {
        toast(`${f.name} is too large`);
        continue;
      }
      const text = await f.text();
      const id = lib.addFromSvg(text, f.name);
      if (id) {
        engine.setParam('stamp', 'set', id);
        toast(`Imported ${f.name.replace(/\.svg$/i, '')}`);
      }
    }
  };

  fileInput.addEventListener('change', () => {
    if (fileInput.files) void importFiles(fileInput.files);
    fileInput.value = '';
  });
  drop.addEventListener('dragover', (e) => {
    e.preventDefault();
    e.stopPropagation();
    drop.classList.add('over');
  });
  drop.addEventListener('dragleave', () => drop.classList.remove('over'));
  drop.addEventListener('drop', (e) => {
    e.preventDefault();
    e.stopPropagation(); // don't let the app-level fallback re-import the same drop
    drop.classList.remove('over');
    if (e.dataTransfer?.files) void importFiles(e.dataTransfer.files);
  });

  captureBtn.addEventListener('click', () => {
    const shapes = engine.getLastCommitShapes();
    if (shapes.length === 0) {
      toast('Draw a mark first, then capture it');
      return;
    }
    const id = lib.captureFromShapes(shapes);
    if (id) {
      engine.setParam('stamp', 'set', id);
      toast('Captured your last stroke into a set');
    }
  });

  const refresh = (): void => {
    sets.innerHTML = '';
    for (const set of lib.sets) {
      const row = document.createElement('div');
      row.className = 'alc-lib-set';

      const previews = document.createElement('div');
      previews.className = 'alc-lib-previews';
      for (const item of set.items.slice(0, 6)) previews.appendChild(glyphCanvas(item));

      const meta = document.createElement('div');
      meta.className = 'alc-lib-meta';
      if (set.builtin) {
        const name = document.createElement('span');
        name.className = 'alc-lib-name';
        name.textContent = set.name;
        meta.appendChild(name);
      } else {
        const name = document.createElement('input');
        name.className = 'alc-lib-name-input';
        name.value = set.name;
        name.addEventListener('change', () => lib.renameSet(set.id, name.value));
        meta.appendChild(name);
      }
      const count = document.createElement('span');
      count.className = 'alc-lib-count';
      count.textContent = `${set.items.length} shape${set.items.length === 1 ? '' : 's'}`;
      meta.appendChild(count);

      const actions = document.createElement('div');
      actions.className = 'alc-lib-actions';
      const use = document.createElement('button');
      use.type = 'button';
      use.className = 'alc-lib-btn';
      use.textContent = 'Use';
      use.addEventListener('click', () => {
        engine.setParam('stamp', 'set', set.id);
        toast(`Stamping with “${set.name}” — pick the Stamp tool`);
      });
      actions.appendChild(use);
      if (!set.builtin) {
        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'alc-lib-btn ghost';
        del.textContent = 'Delete';
        del.addEventListener('click', () => {
          lib.deleteSet(set.id);
          refresh();
        });
        actions.appendChild(del);
      }

      row.append(previews, meta, actions);
      sets.appendChild(row);
    }
  };

  const open = (): void => {
    root.hidden = false;
    refresh();
  };

  return { el: root, open, refresh, importFiles };
}
